import type { ReactNode } from 'react'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import {
	BETA_FEATURES_TAG,
	INVENTORY_TAG,
	LOCATION_ANALYSIS_TAG,
	PASSCODE_TAG,
} from '@micromart/utils/lib/feature-flags'
import { useList, useOne } from '@refinedev/core'
import debounce from 'lodash/debounce'
import noop from 'lodash/noop'

import type { Operator } from '@/types/operator'
import { getOperatorCookie, setRequestOperatorId } from '@/lib/operator'
import { useUser } from '@/hooks/use-user'

const PAGE_SIZE = 50
const ALL_OPERATOR_ID = 0
export const GLOBAL_ACCESS_ROLE = 'global-access'

/**
 * Beta feature tags — the source of truth is `@micromart/utils/lib/feature-flags`.
 * Re-exported here so existing consumers keep importing from workspaces-context.
 */
export {
	AI_SEARCH_TAG,
	BETA_FEATURES_TAG,
	BOGO_PROMOS_TAG,
	CABINET_HEALTH_TAG,
	CABINET_PRICING_TAG,
	CANVA_TAG,
	ENTERPRISE_API_TAG,
	ENTERPRISE_MCP_TAG,
	ETAG_DESIGNS_TAG,
	FOOT_TRAFFIC_TAG,
	GOOGLE_BUSINESS_TAG,
	INCLUDED_TAG,
	INTEGRATIONS_TAG,
	INVENTORY_TAG,
	LOCATION_ANALYSIS_TAG,
	MARKET_INSIGHTS_TAG,
	PASSCODE_TAG,
	QUICKBOOKS_TAG,
	RESOLVED_BY_CABINET_TAG,
	SHIELD_TAG,
	SMART_RESTART_TAG,
	TOP_DISPLAY_PREVIEW_TAG,
	TOP_DISPLAY_PROMOS_TAG,
	VDI_TAG,
} from '@micromart/utils/lib/feature-flags'

export interface WorkspacesContextValue {
	currentOperator?: Operator
	isGlobalOperator: boolean
	isAllOperator: boolean
	hasTaxesEnabled: boolean
	hasBetaFeaturesEnabled: boolean
	hasInventoryEnabled: boolean
	hasPasscodeEnabled: boolean
	hasLocationAnalysisEnabled: boolean
	operators: Operator[]
	isLoading: boolean
	isOperatorsLoading: boolean
	hasMoreOperators: boolean
	canSwitchOperator: boolean
	operatorSearch: string
	setOperatorSearch: (value: string) => void
	loadMoreOperators: () => void
	updateOperator: (operatorId: number) => void
}

export const WorkspacesContext = createContext<WorkspacesContextValue>({
	currentOperator: undefined,
	isGlobalOperator: false,
	isAllOperator: false,
	hasTaxesEnabled: false,
	hasBetaFeaturesEnabled: false,
	hasInventoryEnabled: false,
	hasPasscodeEnabled: false,
	hasLocationAnalysisEnabled: false,
	operators: [],
	isLoading: false,
	isOperatorsLoading: false,
	hasMoreOperators: false,
	canSwitchOperator: false,
	operatorSearch: '',
	setOperatorSearch: noop,
	loadMoreOperators: noop,
	updateOperator: noop,
})

export interface WorkspacesProviderProps {
	children: ReactNode
}

export function WorkspacesProvider({ children }: WorkspacesProviderProps) {
	const { user } = useUser()
	const userHasGlobalAccess = !!user?.roles?.includes(GLOBAL_ACCESS_ROLE)
	const hasBetaFeaturesEnabled = !!user?.tags?.includes(BETA_FEATURES_TAG)
	const hasInventoryEnabled = !!user?.tags?.includes(INVENTORY_TAG)
	const hasPasscodeEnabled = !!user?.tags?.includes(PASSCODE_TAG)
	const hasLocationAnalysisEnabled = !!user?.tags?.includes(LOCATION_ANALYSIS_TAG)
	const [currentOperator, setCurrentOperator] = useState<Operator | undefined>(user?.portal_operator)
	const [isGlobalOperator, setIsGlobalOperator] = useState<boolean>(false)
	const [isAllOperator, setIsAllOperator] = useState<boolean>(false)
	const [hasTaxesEnabled, setHasTaxesEnabled] = useState<boolean>(false)
	const [operatorSearch, setOperatorSearchState] = useState('')
	const [debouncedOperatorSearch, setDebouncedOperatorSearch] = useState('')
	const [page, setPage] = useState(1)
	const [operators, setOperators] = useState<Operator[]>([])

	const { data: currentOperatorData, isLoading: isCurrentLoading } = useOne<Operator>({
		resource: 'operators',
		id: 'current',
	})

	const {
		data: allOperatorData,
		isLoading: isAllLoading,
		isFetching: isAllFetching,
	} = useList<Operator>({
		resource: 'operators/all',
		meta: {
			query: {
				is_active: true,
				...(debouncedOperatorSearch ? { filter: debouncedOperatorSearch } : {}),
			},
		},
		pagination: {
			current: page,
			pageSize: PAGE_SIZE,
		},
		queryOptions: {
			enabled: userHasGlobalAccess,
		},
	})

	const hasMoreOperators = Boolean(allOperatorData?.hasNextPage)
	const isOperatorsLoading = userHasGlobalAccess && (isAllLoading || isAllFetching)
	const isLoading = isCurrentLoading || (userHasGlobalAccess && isAllLoading && page === 1 && !operators.length)

	const setOperatorSearch = useCallback((value: string) => {
		setOperatorSearchState(value)
	}, [])

	const loadMoreOperators = useCallback(() => {
		if (!hasMoreOperators || isAllFetching) return
		setPage((prev) => prev + 1)
	}, [hasMoreOperators, isAllFetching])

	const debouncedSetOperatorSearch = useMemo(
		() =>
			debounce((value: string) => {
				setDebouncedOperatorSearch(value.trim())
			}, 300),
		[]
	)

	useEffect(() => {
		debouncedSetOperatorSearch(operatorSearch)
		return () => {
			debouncedSetOperatorSearch.cancel()
		}
	}, [operatorSearch, debouncedSetOperatorSearch])

	useEffect(() => {
		setPage(1)
	}, [debouncedOperatorSearch])

	useEffect(() => {
		if (isAllLoading || isAllFetching) return
		const pageOperators = allOperatorData?.data
		if (!pageOperators) return

		setOperators((prev) => {
			if (page === 1) return pageOperators
			const seen = new Set(prev.map((operator) => operator.id))
			return [...prev, ...pageOperators.filter((operator) => !seen.has(operator.id))]
		})
	}, [allOperatorData?.data, page, isAllLoading, isAllFetching])

	const handleUpdateOperator = useCallback(
		(operatorId: number) => {
			const operator = operators.find((candidate) => operatorId === candidate.id)
			if (operator) {
				setCurrentOperator(operator)
				setRequestOperatorId(operatorId)
			}
		},
		[operators]
	)

	useEffect(() => {
		if (!isLoading && userHasGlobalAccess) {
			const storedOperatorId = getOperatorCookie()
			if (storedOperatorId !== null) {
				handleUpdateOperator(storedOperatorId)
			}
		}
	}, [isLoading, handleUpdateOperator, userHasGlobalAccess])

	useEffect(() => {
		if (currentOperatorData?.data) {
			// Update currentOperator when data is fetched/refetched
			// Only update if it's the same operator (refetch) or we're initializing
			if (!currentOperator || currentOperator.id === currentOperatorData.data.id) {
				setCurrentOperator(currentOperatorData.data)
				setRequestOperatorId(currentOperatorData.data.id)
			}
		}
	}, [currentOperatorData?.data, currentOperator])

	useEffect(() => {
		if (currentOperator) {
			setIsGlobalOperator(!!currentOperator.is_global)
			setIsAllOperator(currentOperator.id === ALL_OPERATOR_ID)
			setHasTaxesEnabled(!!currentOperator.taxes_enabled_at)
		}
	}, [currentOperator])

	const value = useMemo(
		() => ({
			currentOperator,
			isGlobalOperator,
			isAllOperator,
			hasTaxesEnabled,
			hasBetaFeaturesEnabled,
			hasInventoryEnabled,
			hasPasscodeEnabled,
			hasLocationAnalysisEnabled,
			operators,
			isLoading,
			isOperatorsLoading,
			hasMoreOperators,
			canSwitchOperator: userHasGlobalAccess,
			operatorSearch,
			setOperatorSearch,
			loadMoreOperators,
			updateOperator: handleUpdateOperator,
		}),
		[
			currentOperator,
			isGlobalOperator,
			isAllOperator,
			hasTaxesEnabled,
			hasBetaFeaturesEnabled,
			hasInventoryEnabled,
			hasPasscodeEnabled,
			hasLocationAnalysisEnabled,
			operators,
			isLoading,
			isOperatorsLoading,
			hasMoreOperators,
			userHasGlobalAccess,
			operatorSearch,
			setOperatorSearch,
			loadMoreOperators,
			handleUpdateOperator,
		]
	)

	return <WorkspacesContext.Provider value={value}>{children}</WorkspacesContext.Provider>
}
